import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  Skeleton,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import TimerIcon from "@mui/icons-material/Timer";
import UserStatsCard from "./UserStatsCard";
import InstructionsModal from "./InstructionsModal";

const ExerciseHeader = ({
  exercise,
  routineBlock,
  routine,
  day,
  isLoading,
  colors,
  onRegisterClick,
  onRestClick,
  onNavigatePrev,
  onNavigateNext,
  hasPrevExercise,
  hasNextExercise,
  userFilter,
  onUserFilterChange,
}) => {
  const [instructionsOpen, setInstructionsOpen] = useState(false);

  const calculateUserStats = (username) => {
    if (!exercise.history || exercise.history.length === 0) {
      return null;
    }

    const userSessions = exercise.history.filter((h) => h.username === username);
    if (userSessions.length === 0) {
      return null;
    }

    let totalReps = 0;
    let totalWeight = 0;
    let totalSets = 0;
    let maxReps = 0;
    let maxWeight = 0;

    userSessions.forEach((session) => {
      session.setData?.forEach((set) => {
        totalReps += set.reps || 0;
        totalWeight += set.total || 0;
        totalSets += 1;

        if ((set.reps || 0) > maxReps || ((set.reps || 0) === maxReps && (set.total || 0) > maxWeight)) {
          maxReps = set.reps || 0;
          maxWeight = set.total || 0;
        }
      });
    });

    return {
      totalSessions: userSessions.length,
      avgReps: totalSets > 0 ? totalReps / totalSets : 0,
      avgWeight: totalSets > 0 ? totalWeight / totalSets : 0,
      maxReps,
      maxWeight,
    };
  };

  const renderSupersetInfo = () => {
    const supersetBlock = routine?.description?.[day]?.find(
      (block) =>
        block.type === "superset" &&
        block.exercises.some((ex) => ex.exerciseId === routineBlock.exerciseId)
    );

    const relatedExercises = supersetBlock?.exercises?.filter(
      (ex) => ex.exerciseId !== routineBlock.exerciseId
    );

    return (
      <Box
        sx={{
          p: 1.5,
          bgcolor: "action.hover",
          borderRadius: 1,
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        <Typography
          variant="caption"
          fontWeight="bold"
          sx={{ mb: 1, display: "block", color: "primary.main" }}
        >
          SUPERSET - Se alterna con:
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {relatedExercises?.map((ex, idx) => (
            <Box
              key={`${ex.exerciseId}-${idx}`}
              sx={{
                display: "flex",
                gap: 1.5,
                p: 1,
                bgcolor: "background.paper",
                borderRadius: 1,
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              {ex.exercise?.muscle_model && (
                <Box
                  component="img"
                  src={ex.exercise.muscle_model}
                  alt={ex.exercise.name}
                  sx={{ width: 50, height: 50, borderRadius: 1, objectFit: "cover" }}
                />
              )}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="caption" fontWeight="bold" sx={{ display: "block", mb: 0.5 }}>
                  {ex.exercise?.name || "Otro ejercicio"}
                </Typography>
                <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mb: 0.5 }}>
                  <Chip
                    label={`${ex.sets} series`}
                    size="small"
                    sx={{
                      bgcolor: "#fff",
                      color: "#000",
                      border: "2px solid",
                      borderColor: colors.primary[900],
                      fontWeight: "bold",
                      height: 20,
                      fontSize: "0.65rem",
                    }}
                  />
                  <Chip
                    label={`${ex.reps} reps`}
                    size="small"
                    sx={{
                      bgcolor: "#fff",
                      color: "#000",
                      border: "2px solid",
                      borderColor: colors.primary[900],
                      fontWeight: "bold",
                      height: 20,
                      fontSize: "0.65rem",
                    }}
                  />
                </Box>
                {ex.description && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontStyle: "italic" }}>
                    {ex.description}
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={4}>
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
          {isLoading ? (
            <Skeleton variant="rectangular" width="80%" height={300} sx={{ borderRadius: 2 }} />
          ) : (
            <Box
              component="img"
              src={exercise.muscle_model}
              alt="Modelo muscular"
              sx={{ width: "70%", height: "auto", borderRadius: 2 }}
            />
          )}
        </Box>
      </Grid>

      <Grid item xs={12} md={8}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column-reverse", md: "row" },
            alignItems: "center",
            justifyContent: { xs: "center", md: "flex-start" },
            mb: "5px",
          }}
        >
          {isLoading ? (
            <Skeleton variant="text" width="60%" height={40} />
          ) : (
            <Typography
              variant="h4"
              sx={{ textAlign: { xs: "center", md: "left" }, width: { xs: "100%", md: "auto" } }}
            >
              {exercise.name}
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            mb: 2,
            justifyContent: { xs: "center", md: "flex-start" },
            alignItems: "center",
          }}
        >
          {isLoading ? (
            <>
              <Skeleton variant="rounded" width={100} height={32} />
              <Skeleton variant="rounded" width={100} height={32} />
            </>
          ) : (
            <>
              <Chip label={exercise.body_part} color="primary" />
              <Chip
                icon={<InfoOutlinedIcon />}
                label="Instrucciones"
                onClick={() => setInstructionsOpen(true)}
                variant="outlined"
                color="secondary"
                sx={{ cursor: "pointer" }}
              />
            </>
          )}
        </Box>

        {!isLoading && routineBlock && (
          <>
            <Box
              sx={{
                mb: 1,
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                justifyContent: { xs: "center", md: "flex-start" },
                alignItems: "center",
              }}
            >
              <Chip
                label={`${routineBlock.sets} series`}
                size="small"
                sx={{
                  bgcolor: "#fff",
                  color: "#000",
                  border: "2px solid",
                  borderColor: colors.primary[900],
                  fontWeight: "bold",
                }}
              />
              <Chip
                label={`${routineBlock.reps} reps`}
                size="small"
                sx={{
                  bgcolor: "#fff",
                  color: "#000",
                  border: "2px solid",
                  borderColor: colors.primary[900],
                  fontWeight: "bold",
                }}
              />
            </Box>
            {routineBlock.description && (
              <Box
                sx={{
                  mb: 2,
                  textAlign: { xs: "center", md: "left" },
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  <strong>Dinámica:</strong> {routineBlock.description}
                </Typography>
              </Box>
            )}
          </>
        )}

        {!isLoading && routineBlock?.type === "superset" && (
          <Box sx={{ mb: 2 }}>
            {renderSupersetInfo()}
          </Box>
        )}

        {!isLoading && (
          <>
            {/* Estadísticas compactas */}
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: "flex", gap: 1, mb: 1, flexWrap: "wrap", justifyContent: { xs: "center", md: "flex-start" } }}>
                <Chip
                  label="Mis datos"
                  color={userFilter !== "all" ? "primary" : "default"}
                  onClick={() => {
                    const currentUser = localStorage.getItem("currentUser") || "Vannesa Santamaria";
                    onUserFilterChange(currentUser);
                  }}
                  variant={userFilter !== "all" ? "filled" : "outlined"}
                  sx={{ cursor: "pointer" }}
                />
                <Chip
                  label="Todos"
                  color={userFilter === "all" ? "secondary" : "default"}
                  onClick={() => onUserFilterChange("all")}
                  variant={userFilter === "all" ? "filled" : "outlined"}
                  sx={{ cursor: "pointer" }}
                />
              </Box>
              <Grid container spacing={1}>
                {(userFilter === "all" || userFilter === "Vannesa Santamaria") && (
                  <Grid item xs={12} sm={userFilter === "all" ? 6 : 12}>
                    <UserStatsCard
                      username="Vannesa Santamaria"
                      avatarUrl="/vannesa.jpg"
                      stats={calculateUserStats("Vannesa Santamaria")}
                      colors={colors}
                    />
                  </Grid>
                )}
                {(userFilter === "all" || userFilter === "David Acevedo") && (
                  <Grid item xs={12} sm={userFilter === "all" ? 6 : 12}>
                    <UserStatsCard
                      username="David Acevedo"
                      avatarUrl="/david.jpg"
                      stats={calculateUserStats("David Acevedo")}
                      colors={colors}
                    />
                  </Grid>
                )}
              </Grid>
            </Box>

            <InstructionsModal
              open={instructionsOpen}
              onClose={() => setInstructionsOpen(false)}
              exercise={exercise}
            />

            <Button
              variant="contained"
              onClick={onRegisterClick}
              fullWidth
              size="large"
              sx={{ mt: 2, mb: 1 }}
            >
              Registrar series
            </Button>

            <Box
              sx={{
                display: "flex",
                gap: 1,
                mt: 2,
                justifyContent: { xs: "center", md: "flex-start" },
                alignItems: "center",
              }}
            >
              <Tooltip title="Ejercicio anterior">
                <span>
                  <IconButton
                    color="primary"
                    disabled={!hasPrevExercise}
                    onClick={onNavigatePrev}
                    sx={{ border: "1px solid", borderColor: "divider" }}
                  >
                    <NavigateBeforeIcon />
                  </IconButton>
                </span>
              </Tooltip>

              <Tooltip title="Temporizador de descanso">
                <IconButton
                  color="secondary"
                  onClick={onRestClick}
                  sx={{ border: "1px solid", borderColor: "divider" }}
                >
                  <TimerIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title="Siguiente ejercicio">
                <span>
                  <IconButton
                    color="primary"
                    disabled={!hasNextExercise}
                    onClick={onNavigateNext}
                    sx={{ border: "1px solid", borderColor: "divider" }}
                  >
                    <NavigateNextIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </>
        )}
      </Grid>
    </Grid>
  );
};

export default ExerciseHeader;
